"use client";

import { useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { BasicTextEditorConfigs } from "./configs/constants";
import useEditor from "./hooks/use-editor";
import { API, SERVER_DOMAIN } from "../../../assets/staticData";
import { toast } from "react-toastify";

const TextEditor = (props) => {
  const { init, onChange, value, initialValue, uploaderFileOptions, placeholder } = props;

  const [show, setShow] = useState(true);

  const { openFileUploaderPopup } = useEditor({
    uploaderFileOptions,
  });

  const editorRef = useRef(null);

  const onChangeHandler = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.getContent()); // Log editor content
    }
  };

  const onUpload = (blobInfo) => {
    //    debugger;
    return new Promise((resolve, reject) => {
      let userToken = localStorage.getItem("userToken");
      const toastId = toast(`File Uploading...`, { type: "info", isLoading: true, autoClose: false, });

      const formData = new FormData();
      formData.append("files", blobInfo, blobInfo.name);

      fetch(`${API}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        body: formData,
      })
        .then(async (res) => {
          if (!res.ok) {
            throw new Error("Failed to upload image");
          }
          const response = await res.json();
          const imageUrl = `${SERVER_DOMAIN}${response[0]?.url}`;
          if (imageUrl) {
            toast.dismiss(toastId)
            toast(`File Uploaded successfully.`, { type: "success" });

            resolve(imageUrl);
          }
        })
        .catch((e) => {
          toast.dismiss(toastId)

          toast(`Unable Upload File. Please try again`, { type: "error" });
          reject("No URL returned from upload.");
        });
    });
  };

  const onUploadHandler = (callback, value, meta) => {
    openFileUploaderPopup(async (file) => {
      const result = await onUpload(file, value, meta);
      if (result) {
        //  debugger;
        callback(result, meta);
      }
    });
  };

  const rerender = () => {
    setShow((prevShow) => !prevShow);
  };

  useEffect(() => {
    !show && rerender();
  }, [show]);

  const setupFontWeightsToolbarButton = (editor) => {
    const weights = [
      { weight: '100', text: 'Thin' },
      { weight: '300', text: 'Light' },
      { weight: '400', text: 'Regular' },
      { weight: '500', text: 'Medium' },
      { weight: '700', text: 'Bold' },
      { weight: '900', text: 'Black' }
    ];

    editor.ui.registry.addMenuButton('fontweights', {
      text: 'Font Weight', // Default text
      onSetup: (buttonApi) => {
        const updateButtonText = () => {
          const node = editor.selection.getNode();
          const currentWeight = editor.dom.getStyle(node, 'font-weight');

          // Find the matching weight object
          const matchingWeight = weights.find(w => w.weight === currentWeight);

          if (matchingWeight) {
            buttonApi.setText(matchingWeight.text);
          } else {
            buttonApi.setText('Font Weight');
          }
        };

        // Listen for changes in the editor to update the label
        editor.on('NodeChange', updateButtonText);

        // Initial check
        updateButtonText();

        return () => editor.off('NodeChange', updateButtonText);
      },
      fetch: (callback) => {
        const items = weights.map(item => ({
          type: 'togglemenuitem',
          text: `${item.text} (${item.weight})`,
          onAction: () => {
            const formatName = `custom_weight_${item.weight}`;

            if (!editor.formatter.get(formatName)) {
              editor.formatter.register(formatName, {
                inline: 'span',
                styles: { 'font-weight': item.weight }
              });
            }

            editor.formatter.apply(formatName);
          },
          onSetup: (itemApi) => {
            const checkState = () => {
              const node = editor.selection.getNode();
              const currentWeight = editor.dom.getStyle(node, 'font-weight');
              itemApi.setActive(currentWeight === item.weight);
            };

            editor.on('NodeChange', checkState);
            checkState();
            return () => editor.off('NodeChange', checkState);
          }
        }));

        callback(items);
      },
    });
  }

  const onSetup = (editor) => {
    setupFontWeightsToolbarButton(editor)
  };

  const onInit = (_evt, editor) => {
    editorRef.current = editor
  }

  return show ? (
    <Editor
      tinymceScriptSrc="/tinymce/tinymce.min.js"
      licenseKey="gpl"
      onEditorChange={onChangeHandler}
      onInit={onInit}
      init={{
        setup: onSetup,
        menubar: "file edit view insert format tools table help",
        plugins: BasicTextEditorConfigs.plugins,
        toolbar: BasicTextEditorConfigs.toolbar,
        font_family_formats: BasicTextEditorConfigs.fontFamilyFormats,
        font_size_formats: '12px 14px 16px 18px 24px 36px 48px 8pt 10pt 12pt 14pt 18pt 24pt 36pt',
        content_style: BasicTextEditorConfigs.contentStyle,
        image_caption: true,
        min_height: "500px",
        placeholder,
        quickbars_selection_toolbar: false,
        quickbars_insert_toolbar: false,
        ...init,
        file_picker_callback: onUploadHandler,
        paste_data_images: false,
      }}
      initialValue={initialValue}
      value={value}
    />
  ) : (
    <></>
  );
};

export default TextEditor;
